import React, { useState, useEffect, useRef } from "react";
import Modal from "react-modal";
import { WebshopService } from "../api/generated/services/WebshopService";
import { ProductItem } from "../api/generated/models/ProductItem";
import { PurchaseDto } from "../api/generated/models/PurchaseDto";
import { PurchaseStatus } from "../api/generated/models/PurchaseStatus";
import { formatNumber } from "../helpers/formatNumber";
import { Button } from "../components/Button";
import { ReactComponent as CloseIcon } from "../assets/icons/x.svg";
import "../api/openApiConfig";

const StatusBadge: React.FC<{ status?: PurchaseStatus }> = ({ status }) => {
  switch (status) {
    case PurchaseStatus.SUCCESS:
      return (
        <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
          Success
        </span>
      );
    case PurchaseStatus.FAILED:
      return (
        <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
          Failed
        </span>
      );
    case PurchaseStatus.PENDING:
      return (
        <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
          Pending
        </span>
      );
    default:
      return <span className="text-gray-400">-</span>;
  }
};

const PurchaseStatusBadge: React.FC<{ purchase: PurchaseDto }> = ({ purchase }) => {
  if (purchase.isPending) {
    return (
      <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
        Pending
      </span>
    );
  }
  if (purchase.hasErrors) {
    return (
      <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
        Failed
      </span>
    );
  }
  if (purchase.isComplete) {
    return (
      <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
        Complete
      </span>
    );
  }
  return <span className="text-gray-400">-</span>;
};

interface PurchaseActionsPopoverProps {
  purchase: PurchaseDto;
  onViewDetails: () => void;
  onRetry: () => void;
  onResend: () => void;
}

const PurchaseActionsPopover: React.FC<PurchaseActionsPopoverProps> = ({
  purchase,
  onViewDetails,
  onRetry,
  onResend,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [popoverPosition, setPopoverPosition] = useState({ top: 0, left: 0 });
  const popoverRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLDivElement>(null);

  const isHuuray = purchase.provider?.toLowerCase() === "huuray";
  const provisioningFailed =
    purchase.productProvisioningStatus?.status === PurchaseStatus.FAILED;
  const provisioningSuccess =
    purchase.productProvisioningStatus?.status === PurchaseStatus.SUCCESS;

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        popoverRef.current &&
        !popoverRef.current.contains(event.target as Node) &&
        buttonRef.current &&
        !buttonRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isOpen]);

  useEffect(() => {
    if (isOpen && buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect();
      setPopoverPosition({
        top: rect.bottom + 8,
        left: rect.right - 192,
      });
    }
  }, [isOpen]);

  const handleAction = (action: () => void) => {
    action();
    setIsOpen(false);
  };

  return (
    <>
      <div ref={buttonRef}>
        <Button
          variant="icon"
          onClick={() => setIsOpen(!isOpen)}
          className="p-2"
        >
          <svg
            width="16"
            height="16"
            viewBox="0 0 16 16"
            fill="currentColor"
            xmlns="http://www.w3.org/2000/svg"
          >
            <circle cx="8" cy="3" r="1.5" />
            <circle cx="8" cy="8" r="1.5" />
            <circle cx="8" cy="13" r="1.5" />
          </svg>
        </Button>
      </div>

      {isOpen && (
        <div
          ref={popoverRef}
          className="fixed w-48 bg-white rounded-lg shadow-lg border border-gray-200 py-2 z-[9999]"
          style={{
            top: `${popoverPosition.top}px`,
            left: `${popoverPosition.left}px`,
          }}
        >
          <button
            onClick={() => handleAction(onViewDetails)}
            className="w-full text-left px-4 py-2 hover:bg-gray-100 cursor-pointer text-sm"
          >
            View Details
          </button>
          {isHuuray && provisioningFailed && (
            <button
              onClick={() => handleAction(onRetry)}
              className="w-full text-left px-4 py-2 hover:bg-gray-100 cursor-pointer text-sm"
            >
              Retry Provisioning
            </button>
          )}
          {isHuuray && provisioningSuccess && (
            <button
              onClick={() => handleAction(onResend)}
              className="w-full text-left px-4 py-2 hover:bg-gray-100 cursor-pointer text-sm"
            >
              Resend Gift Card
            </button>
          )}
        </div>
      )}
    </>
  );
};

interface PurchaseDetailsModalProps {
  purchase: PurchaseDto | null;
  onClose: () => void;
}

const PurchaseDetailsModal: React.FC<PurchaseDetailsModalProps> = ({
  purchase,
  onClose,
}) => {
  if (!purchase) return null;

  return (
    <Modal
      className="modal-content md:max-w-[600px]"
      overlayClassName="modal-overlay"
      isOpen={!!purchase}
      onRequestClose={onClose}
      contentLabel="Purchase Details"
    >
      <div className="flex justify-between items-start mb-6">
        <h2 className="text-primary font-medium text-xl">Purchase Details</h2>
        <Button onClick={onClose} variant="icon">
          <CloseIcon className="w-3 h-3" />
        </Button>
      </div>

      <div className="grid grid-cols-2 gap-x-6 gap-y-4 text-sm">
        <div>
          <span className="text-gray-500 block">Purchase ID</span>
          <span className="font-medium break-all">{purchase.id ?? "-"}</span>
        </div>
        <div>
          <span className="text-gray-500 block">Product ID</span>
          <span className="font-medium break-all">{purchase.productId ?? "-"}</span>
        </div>
        <div>
          <span className="text-gray-500 block">Provider</span>
          <span className="font-medium uppercase">{purchase.provider ?? "-"}</span>
        </div>
        <div>
          <span className="text-gray-500 block">Customer ID</span>
          <span className="font-medium break-all">{purchase.customerId ?? "-"}</span>
        </div>
        <div>
          <span className="text-gray-500 block">Merchant</span>
          <span className="font-medium">{purchase.merchantName ?? "-"}</span>
          {purchase.merchantId && (
            <span className="text-xs text-gray-500 block break-all">{purchase.merchantId}</span>
          )}
        </div>
        <div>
          <span className="text-gray-500 block">Company</span>
          <span className="font-medium">{purchase.companyName ?? "-"}</span>
          {purchase.companyId && (
            <span className="text-xs text-gray-500 block break-all">{purchase.companyId}</span>
          )}
        </div>
        <div>
          <span className="text-gray-500 block">Amount</span>
          <span className="font-medium">
            {purchase.amount != null
              ? `${formatNumber(purchase.amount)} ${purchase.currency ?? ""}`
              : "-"}
          </span>
        </div>
        <div>
          <span className="text-gray-500 block">Charge Handle</span>
          <span className="font-medium break-all">{purchase.chargeHandle ?? "-"}</span>
        </div>
        <div>
          <span className="text-gray-500 block">Created At</span>
          <span className="font-medium">
            {purchase.createdAt
              ? new Date(purchase.createdAt).toLocaleString()
              : "-"}
          </span>
        </div>
        <div>
          <span className="text-gray-500 block">Settled At</span>
          <span className="font-medium">
            {purchase.settledAt
              ? new Date(purchase.settledAt).toLocaleString()
              : "-"}
          </span>
        </div>
        <div>
          <span className="text-gray-500 block">Publishing Status</span>
          <div className="mt-1">
            <StatusBadge status={purchase.publishingStatus?.status} />
            {purchase.publishingStatus?.lastErrorMessage && (
              <p className="text-xs text-red-600 mt-1">
                {purchase.publishingStatus.lastErrorMessage}
              </p>
            )}
            {purchase.publishingStatus?.lastErrorCode && (
              <p className="text-xs text-gray-500">
                Code: {purchase.publishingStatus.lastErrorCode}
              </p>
            )}
          </div>
        </div>
        <div>
          <span className="text-gray-500 block">Product Provisioning</span>
          <div className="mt-1">
            <StatusBadge status={purchase.productProvisioningStatus?.status} />
            {purchase.productProvisioningStatus?.lastErrorMessage && (
              <p className="text-xs text-red-600 mt-1">
                {purchase.productProvisioningStatus.lastErrorMessage}
              </p>
            )}
            {purchase.productProvisioningStatus?.lastErrorCode && (
              <p className="text-xs text-gray-500">
                Code: {purchase.productProvisioningStatus.lastErrorCode}
              </p>
            )}
          </div>
        </div>
      </div>

      <div className="flex justify-end mt-6">
        <Button variant="outline" onClick={onClose}>
          Close
        </Button>
      </div>
    </Modal>
  );
};

const AdminWebshop: React.FC = () => {
  const [products, setProducts] = useState<ProductItem[]>([]);
  const [purchases, setPurchases] = useState<PurchaseDto[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [copiedField, setCopiedField] = useState<string | null>(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [productsResponse, purchasesResponse] = await Promise.all([
          WebshopService.postApiWebshopListProducts(),
          WebshopService.postApiWebshopListPurchaseHistory(),
        ]);
        setProducts(productsResponse.items ?? []);
        setPurchases(purchasesResponse ?? []);
      } catch (err) {
        console.error("Error fetching webshop data:", err);
        setError("Failed to fetch webshop data");
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  const [selectedPurchase, setSelectedPurchase] = useState<PurchaseDto | null>(null);
  const [confirmAction, setConfirmAction] = useState<"retry" | "resend" | null>(null);
  const [actionPurchase, setActionPurchase] = useState<PurchaseDto | null>(null);
  const [actionLoading, setActionLoading] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);
  const [actionSuccess, setActionSuccess] = useState<string | null>(null);

  const [refreshingPurchases, setRefreshingPurchases] = useState(false);

  const refreshPurchases = async () => {
    setRefreshingPurchases(true);
    try {
      const response = await WebshopService.postApiWebshopListPurchaseHistory();
      setPurchases(response ?? []);
    } catch (err) {
      console.error("Error fetching purchases:", err);
    } finally {
      setRefreshingPurchases(false);
    }
  };

  const handleRetry = async () => {
    if (!actionPurchase?.id) return;
    setActionLoading(true);
    setActionError(null);
    setActionSuccess(null);
    try {
      await WebshopService.postApiWebshopRetryProvisioningHuurayGiftCard({
        id: actionPurchase.id,
      });
      setActionSuccess("Provisioning is being retried. The status will update shortly.");
      await refreshPurchases();
    } catch (err) {
      console.error("Error retrying provisioning:", err);
      setActionError("Failed to retry provisioning. Please try again.");
    } finally {
      setActionLoading(false);
    }
  };

  const handleResend = async () => {
    if (!actionPurchase?.id) return;
    setActionLoading(true);
    setActionError(null);
    setActionSuccess(null);
    try {
      await WebshopService.postApiWebshopResendHuurayGiftCard({
        id: actionPurchase.id,
      });
      setActionSuccess("Gift card has been successfully resent to the user.");
      await refreshPurchases();
    } catch (err) {
      console.error("Error resending gift card:", err);
      setActionError("Failed to resend gift card. Please try again.");
    } finally {
      setActionLoading(false);
    }
  };

  const handleCopy = async (value: string, fieldKey: string) => {
    try {
      await navigator.clipboard.writeText(value);
      setCopiedField(fieldKey);
      setTimeout(() => setCopiedField(null), 2000);
    } catch (err) {
      console.error("Failed to copy:", err);
    }
  };

  const CopyButton: React.FC<{ value: string; fieldKey: string }> = ({ value, fieldKey }) => (
    <button
      onClick={() => handleCopy(value, fieldKey)}
      className="p-1 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded transition-colors flex-shrink-0"
      title="Copy to clipboard"
      type="button"
    >
      {copiedField === fieldKey ? (
        <svg className="w-4 h-4 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
        </svg>
      ) : (
        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
        </svg>
      )}
    </button>
  );

  if (loading) {
    return <div className="loading-page">Loading...</div>;
  }

  return (
    <>
      <div className="mb-8 lg:mb-12">
        <h2 className="text-black/60">Webshop</h2>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-800 px-4 py-3 rounded-lg text-sm mb-6">
          {error}
        </div>
      )}

      <div className="bg-white rounded-2xl p-6">
        <h3 className="text-lg font-medium mb-4">Products</h3>
        {products.length === 0 ? (
          <p className="text-sm text-gray-500">No products found.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="table-default">
              <thead>
                <tr>
                  <th>Product Name</th>
                  <th>Provider</th>
                  <th>Unit Price</th>
                  <th>Currency</th>
                  <th>VAT %</th>
                  <th>Price Incl. VAT</th>
                  <th>Custom Link</th>
                </tr>
              </thead>
              <tbody>
                {products.map((product) => (
                  <tr key={product.productId}>
                    <td className="uppercase">{product.productName ?? "-"}</td>
                    <td className="uppercase">{product.provider ?? "-"}</td>
                    <td>{product.unitPrice != null ? formatNumber(product.unitPrice) : "-"}</td>
                    <td>{product.currency ?? "-"}</td>
                    <td>
                      {product.vatPercentage != null
                        ? `${product.vatPercentage}%`
                        : "-"}
                    </td>
                    <td>{product.priceIncludesVat ? "Yes" : "No"}</td>
                    <td>
                      <div className="flex items-center gap-2">
                        <span
                          className="truncate max-w-[200px]"
                          title={product.webshopUrl ?? ""}
                        >
                          {product.webshopUrl ?? "-"}
                        </span>
                        {product.webshopUrl && (
                          <CopyButton value={product.webshopUrl} fieldKey={`product-url-${product.productId}`} />
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <div className="bg-white rounded-2xl p-6 mt-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-medium">Purchase History</h3>
          <button
            onClick={refreshPurchases}
            disabled={refreshingPurchases}
            className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors disabled:opacity-50"
            title="Refresh purchase history"
            type="button"
          >
            <svg
              className={`w-5 h-5 ${refreshingPurchases ? "animate-spin" : ""}`}
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
              />
            </svg>
          </button>
        </div>
        {purchases.length === 0 ? (
          <p className="text-sm text-gray-500">No purchases found.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="table-default">
              <thead>
                <tr>
                  <th>Purchased At</th>
                  <th>Product ID / Provider</th>
                  <th>Business</th>
                  <th>Customer ID</th>
                  <th>Price</th>
                  <th>Invoice ID</th>
                  <th>Status</th>
                  <th>Cashback Assignment</th>
                  <th>Product Provisioning</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {purchases.map((purchase) => (
                  <tr key={purchase.id}>
                    <td>
                      {purchase.settledAt
                        ? new Date(purchase.settledAt).toLocaleString()
                        : "-"}
                    </td>
                    <td>
                      <div className="uppercase">{purchase.productId ?? "-"}</div>
                      {purchase.provider && (
                        <div className="text-xs text-gray-500 uppercase">
                          {purchase.provider}
                        </div>
                      )}
                    </td>
                    <td>
                      {purchase.merchantName && (
                        <div>{purchase.merchantName}</div>
                      )}
                      <div className="flex items-center gap-1">
                        <span className="text-xs text-gray-500">{purchase.merchantId ?? "-"}</span>
                        {purchase.merchantId && (
                          <CopyButton value={purchase.merchantId} fieldKey={`merchant-${purchase.id}`} />
                        )}
                      </div>
                    </td>
                    <td>
                      <div className="flex items-center gap-1">
                        <span>{purchase.customerId ?? "-"}</span>
                        {purchase.customerId && (
                          <CopyButton value={purchase.customerId} fieldKey={`customer-${purchase.id}`} />
                        )}
                      </div>
                    </td>
                    <td>
                      {purchase.amount != null
                        ? `${formatNumber(purchase.amount)} ${purchase.currency ?? ""}`
                        : "-"}
                    </td>
                    <td>
                      <div className="flex items-center gap-1">
                        <span
                          className="truncate max-w-[150px] inline-block"
                          title={purchase.chargeHandle ?? ""}
                        >
                          {purchase.chargeHandle ?? "-"}
                        </span>
                        {purchase.chargeHandle && (
                          <CopyButton value={purchase.chargeHandle} fieldKey={`invoice-${purchase.id}`} />
                        )}
                      </div>
                    </td>
                    <td>
                      <PurchaseStatusBadge purchase={purchase} />
                    </td>
                    <td>
                      <StatusBadge
                        status={purchase.publishingStatus?.status}
                      />
                    </td>
                    <td>
                      <StatusBadge
                        status={
                          purchase.productProvisioningStatus?.status
                        }
                      />
                    </td>
                    <td>
                      <PurchaseActionsPopover
                        purchase={purchase}
                        onViewDetails={() => setSelectedPurchase(purchase)}
                        onRetry={() => {
                          setActionPurchase(purchase);
                          setConfirmAction("retry");
                        }}
                        onResend={() => {
                          setActionPurchase(purchase);
                          setConfirmAction("resend");
                        }}
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <PurchaseDetailsModal
        purchase={selectedPurchase}
        onClose={() => setSelectedPurchase(null)}
      />

      <Modal
        className="modal-content md:max-w-[400px]"
        overlayClassName="modal-overlay"
        isOpen={confirmAction !== null}
        onRequestClose={() => {
          if (!actionLoading) {
            setConfirmAction(null);
            setActionPurchase(null);
            setActionError(null);
            setActionSuccess(null);
          }
        }}
        contentLabel={confirmAction === "retry" ? "Retry Provisioning" : "Resend Gift Card"}
      >
        <div className="flex justify-between items-start mb-4">
          <h2 className="text-primary font-medium text-xl">
            {confirmAction === "retry" ? "Retry Provisioning" : "Resend Gift Card"}
          </h2>
          <Button
            onClick={() => {
              setConfirmAction(null);
              setActionPurchase(null);
              setActionError(null);
              setActionSuccess(null);
            }}
            variant="icon"
            disabled={actionLoading}
          >
            <CloseIcon className="w-3 h-3" />
          </Button>
        </div>

        {actionSuccess ? (
          <>
            <div className="bg-green-50 border border-green-200 text-green-800 px-4 py-3 rounded-lg text-sm mb-6">
              {actionSuccess}
            </div>
            <div className="flex justify-end">
              <Button
                type="button"
                variant="primary"
                onClick={() => {
                  setConfirmAction(null);
                  setActionPurchase(null);
                  setActionSuccess(null);
                }}
              >
                Done
              </Button>
            </div>
          </>
        ) : (
          <>
            {actionError && (
              <div className="bg-red-50 border border-red-200 text-red-800 px-4 py-3 rounded-lg text-sm mb-4">
                {actionError}
              </div>
            )}
            <p className="text-gray-600 mb-6">
              {confirmAction === "retry"
                ? `Are you sure you want to retry provisioning the Huuray gift card for purchase ${actionPurchase?.id ?? ""}?`
                : `Are you sure you want to resend the Huuray gift card for purchase ${actionPurchase?.id ?? ""}?`}
            </p>
            <div className="flex justify-end gap-3">
              <Button
                type="button"
                variant="outline"
                onClick={() => {
                  setConfirmAction(null);
                  setActionPurchase(null);
                  setActionError(null);
                }}
                disabled={actionLoading}
              >
                Cancel
              </Button>
              <Button
                type="button"
                variant="primary"
                onClick={confirmAction === "retry" ? handleRetry : handleResend}
                isLoading={actionLoading}
                disabled={actionLoading}
              >
                {confirmAction === "retry" ? "Retry" : "Resend"}
              </Button>
            </div>
          </>
        )}
      </Modal>
    </>
  );
};

export default AdminWebshop;
